/**
 * Teach it once (#1738): "Mark done" on a rule's promise. One optional note,
 * then the mark (`POST /api/jobs/:id/promise-checks`): the promise turns kept,
 * with who and when as its proof. Undo lives on the kept row.
 */
import { useEffect, useState } from "react";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export function MarkRuleForm({
    title,
    actionLabel,
    saving = false,
    onSave,
    onCancel,
}: {
    /** The promise's title ("Packet delivered by FWISD vendor portal"). */
    title: string;
    /** The action's own words ("Mark sent by portal", "Mark done"). */
    actionLabel: string;
    saving?: boolean;
    onSave: (note: string | null) => void;
    onCancel: () => void;
}) {
    const [note, setNote] = useState("");
    return (
        <div className="grid gap-4 text-sm" data-testid="mark-rule-form">
            <p className="m-0 font-semibold text-foreground">{title}</p>
            <div className="grid gap-1.5">
                <Label htmlFor="mark-rule-note" className="text-[13px] font-semibold">
                    Note <span className="font-normal text-muted-foreground">(optional)</span>
                </Label>
                <Textarea
                    id="mark-rule-note"
                    className="min-h-[64px] text-sm"
                    value={note}
                    maxLength={1000}
                    placeholder="Uploaded to the portal, confirmation 55120"
                    onChange={(e) => setNote(e.target.value)}
                />
            </div>
            <div className="flex flex-wrap items-center justify-end gap-4">
                <button
                    type="button"
                    className="rounded-sm text-sm font-semibold text-muted-foreground hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    onClick={onCancel}
                >
                    Cancel
                </button>
                <Button type="button" disabled={saving} onClick={() => onSave(note.trim() || null)}>
                    {saving ? "Saving…" : actionLabel}
                </Button>
            </div>
        </div>
    );
}

export function MarkRuleDialog({
    open,
    onClose,
    title,
    actionLabel,
    saving,
    onSave,
}: {
    open: boolean;
    onClose: () => void;
    title: string;
    actionLabel: string;
    saving?: boolean;
    onSave: (note: string | null) => void;
}) {
    // A fresh note each time it opens.
    const [round, setRound] = useState(0);
    useEffect(() => {
        if (open) setRound((n) => n + 1);
    }, [open]);
    return (
        <Dialog open={open} onClose={onClose} title={actionLabel}>
            <MarkRuleForm
                key={round}
                title={title}
                actionLabel={actionLabel}
                saving={saving}
                onSave={onSave}
                onCancel={onClose}
            />
        </Dialog>
    );
}
